// Overview stats: Equity / Safe Factor / Net / Realized / Unrealized / Funding.
// Unrealized is computed from live WS prices across all grid symbols + wind cycles.
import { useCallback, useEffect, useState } from "react";
import { useOverview, type UnpairedTrade } from "@/hooks/use-overview";
import { useAccount } from "@/hooks/use-account";
import { useLivePrice } from "@/providers/ws-provider";
import { fmt } from "@/lib/format";
import StatCard from "@/components/common/stat-card";
import type { Tone } from "@/components/common/stat-card";

const TAKER_FEE_RATE = 0.0007;

function sfTone(sf: number | null): Tone {
  if (sf == null) return "neutral";
  if (sf >= 2) return "success";
  if (sf >= 1.2) return "warn";
  return "danger";
}

function pnlTone(v: number): Tone {
  if (v > 0) return "success";
  if (v < 0) return "danger";
  return "neutral";
}

/**
 * Per-symbol unrealized PnL contributor.
 * Rendered as a zero-size element that calls useLivePrice once per symbol
 * and reports its contribution via the callback — avoids hook-in-loop rule.
 * Uses useEffect so the callback only fires when price/legs change, and the
 * parent's setState causes a proper re-render (previously the object was
 * mutated during render → React never re-rendered → Unrealized stuck at 0).
 */
function SymbolUnrealized({
  symbol,
  legs,
  onValue,
}: {
  symbol: string;
  legs: UnpairedTrade[];
  onValue: (symbol: string, v: number) => void;
}) {
  const price = useLivePrice(symbol);
  useEffect(() => {
    if (!price) return;
    let sum = 0;
    for (const t of legs) {
      const dir = t.side === "buy" ? 1 : -1;
      const closeFee = price * t.quantity * TAKER_FEE_RATE;
      sum += (price - t.price) * t.quantity * dir - (t.fee ?? 0) - closeFee;
    }
    onValue(symbol, sum);
  }, [symbol, price, legs, onValue]);
  return null;
}

export default function OverviewStats() {
  const { data: overview } = useOverview();
  const { data: account } = useAccount();

  // Group unpaired legs by symbol. Grid legs get their symbol via grid_id →
  // grids[] lookup (backend returns full GridConfig; we only need symbol).
  const gridSymbolById = new Map<string, string>();
  for (const g of overview?.grids ?? []) gridSymbolById.set(g.id, g.symbol);

  const bySymbol = new Map<string, UnpairedTrade[]>();
  for (const t of overview?.unpaired ?? []) {
    const sym = gridSymbolById.get(t.grid_id);
    if (!sym) continue; // orphan leg (grid stopped between fetches) — skip
    if (!bySymbol.has(sym)) bySymbol.set(sym, []);
    bySymbol.get(sym)!.push(t);
  }

  // Wind active cycles: group by symbol
  for (const w of overview?.wind_active ?? []) {
    const lc = w.latest_cycle;
    if (!lc || lc.status !== "open" || !lc.entry_price) continue;
    const sym = w.session.symbol;
    if (!bySymbol.has(sym)) bySymbol.set(sym, []);
    // Represent as UnpairedTrade shape for SymbolUnrealized reuse
    bySymbol.get(sym)!.push({
      id: 0,
      grid_id: "",
      side: lc.direction === "long" ? "buy" : "sell",
      price: lc.entry_price,
      quantity: lc.quantity,
      fee: lc.entry_fee,
    });
  }

  // State-driven unrealized aggregation. Each SymbolUnrealized child calls
  // handleUnrealized(symbol, v) inside its useEffect; setState triggers the
  // re-render that recomputes totalUnrealized. Previously this was mutated
  // during render → React never saw the update → card stuck at 0.
  const [unrealizedBySymbol, setUnrealizedBySymbol] = useState<Record<string, number>>({});
  const handleUnrealized = useCallback((symbol: string, v: number) => {
    setUnrealizedBySymbol((prev) =>
      prev[symbol] === v ? prev : { ...prev, [symbol]: v },
    );
  }, []);

  const pnl = overview?.pnl;
  const funding = pnl?.funding_total ?? 0;
  const realized = pnl?.total_pnl ?? 0;

  // Only sum contributions for symbols we still have open positions on — a
  // grid that closed its last leg between renders would otherwise keep its
  // stale unrealized in the reducer.
  let totalUnrealized = 0;
  for (const sym of bySymbol.keys()) totalUnrealized += unrealizedBySymbol[sym] ?? 0;

  const net = realized + totalUnrealized + funding;

  // Equity card
  const equityVal = account ? fmt(account.equity, 2) : "—";
  const equitySub = account
    ? `free ${fmt(account.free, 2)} · used ${account.margin_pct.toFixed(1)}%`
    : "";

  const sf = account?.safe_factor ?? null;
  const sfVal = sf != null ? `${sf.toFixed(2)}×` : "—";
  const sfSub = account ? `${account.account_leverage}× account lev` : "";

  // Net exposure display
  const netExp = account?.net_exposure ?? 0;
  const netExpDir = netExp > 0 ? "LONG" : netExp < 0 ? "SHORT" : "FLAT";
  const netExpVal = account ? `${fmt(Math.abs(netExp), 0)} ${netExpDir}` : "—";
  const netExpSub = account
    ? `L ${fmt(account.long_notional, 0)} / S ${fmt(account.short_notional, 0)}`
    : "";
  const netExpTone: Tone = account?.exposure_suppressed_side ? "warn" : "neutral";
  const suppressLabel = account?.exposure_suppressed_side
    ? ` · ${account.exposure_suppressed_side.toUpperCase()} suppressed`
    : "";

  return (
    <>
      {/* Hidden price subscribers — one per symbol */}
      {Array.from(bySymbol.entries()).map(([sym, legs]) => (
        <SymbolUnrealized
          key={sym}
          symbol={sym}
          legs={legs}
          onValue={handleUnrealized}
        />
      ))}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <StatCard
          label="Equity"
          value={equityVal}
          sub={equitySub}
          tone="neutral"
          title="Trading account equity (USDT)"
        />
        <StatCard
          label="Safe Factor"
          value={sfVal}
          sub={sfSub}
          tone={sfTone(sf)}
          title="Safe factor = equity / maintenance margin. ≥2 safe, 1.2–2 watch, <1.2 danger."
        />
        <StatCard
          label="Net"
          value={pnl ? (net >= 0 ? "+" : "") + fmt(net, 4) : "—"}
          tone={pnl ? pnlTone(net) : "neutral"}
          title="Net PnL = Realized + Unrealized + Funding"
        />
        <StatCard
          label="Realized PnL"
          value={pnl ? fmt(realized, 4) : "—"}
          sub="net of fees"
          tone={pnl ? pnlTone(realized) : "neutral"}
          title="Realized PnL from closed pairs, net of trading fees on both legs"
        />
        <StatCard
          label="Unrealized PnL"
          value={pnl ? fmt(totalUnrealized, 4) : "—"}
          sub="incl. fees"
          tone={pnl ? pnlTone(totalUnrealized) : "neutral"}
          title="Mark-to-market on open positions, including opening fee paid and estimated taker close fee"
        />
        <StatCard
          label="Funding"
          value={pnl ? fmt(funding, 4) : "—"}
          tone={pnl ? pnlTone(funding) : "neutral"}
          title="Perpetual funding fees received/paid (settled every 8h)."
        />
        <StatCard
          label="24h Volume"
          value={overview?.vol_24h ? fmt(overview.vol_24h.volume, 0) : "—"}
          sub={overview?.vol_24h ? `${overview.vol_24h.trades} trades · fee ${fmt(overview.vol_24h.fees, 2)}` : ""}
          tone="neutral"
          title="24-hour trading volume (notional USDT)"
        />
        <StatCard
          label="Net Exposure"
          value={netExpVal}
          sub={netExpSub + suppressLabel}
          tone={netExpTone}
          title="Net directional exposure = long - short notional. Yellow = side suppressed by portfolio guard."
        />
      </div>
    </>
  );
}
